import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import BusLayout from "../../components/BusLayout";
import { API_CONSTANTS } from "../../common/constants/api";
import {
  canPromptInstall,
  isIosBrowser,
  isStandalone,
  promptInstall,
  subscribeInstall,
} from "../../common/installPrompt";
import { IBusLocation, SEAT_LAYOUT, SEAT_STYLE, SeatAvailability } from "../../common/types";
import styles from "./internalDesk.module.scss";

const SESSION_TOKEN = "DESK_SESSION";
const SESSION_UNTIL = "DESK_SESSION_UNTIL";
const SESSION_MOBILE = "DESK_MOBILE";
const REDBUS_EDITOR_MOBILE = "9902263748";

const readStoredSession = () => {
  const token = localStorage.getItem(SESSION_TOKEN) || "";
  const until = Number(localStorage.getItem(SESSION_UNTIL) || "0");
  const mobile = localStorage.getItem(SESSION_MOBILE) || "";
  if (!token || !until || until <= Date.now()) {
    localStorage.removeItem(SESSION_TOKEN);
    localStorage.removeItem(SESSION_UNTIL);
    localStorage.removeItem(SESSION_MOBILE);
    return { token: "", until: 0, mobile: "" };
  }
  return { token, until, mobile };
};

const FROM_TO_OPTIONS = [
  { value: "bk", label: "Bhubaneswar to Kuchinda (bk)" },
  { value: "bd", label: "Bhubaneswar to Deogarh (bd)" },
  { value: "cd", label: "Cuttack to Deogarh (cd)" },
  { value: "ck", label: "Cuttack to Kuchinda (ck)" },
  { value: "kb", label: "Kuchinda to Bhubaneswar (kb)" },
  { value: "db", label: "Deogarh to Bhubaneswar (db)" },
  { value: "dc", label: "Deogarh to Cuttack (dc)" },
  { value: "kc", label: "Kuchinda to Cuttack (kc)" },
];

const FROM_TO_STOPS: Record<string, { from: string; to: string }> = {
  bk: { from: "Bhubaneswar", to: "Kuchinda" },
  bd: { from: "Bhubaneswar", to: "Deogarh" },
  cd: { from: "Cuttack", to: "Deogarh" },
  ck: { from: "Cuttack", to: "Kuchinda" },
  kb: { from: "Kuchinda", to: "Bhubaneswar" },
  db: { from: "Deogarh", to: "Bhubaneswar" },
  dc: { from: "Deogarh", to: "Cuttack" },
  kc: { from: "Kuchinda", to: "Cuttack" },
};

const FORWARD_CODES = new Set(["bd", "bk", "cd", "ck"]);

const directionName = (routeId: string) =>
  routeId === "2" || routeId === "4" ? "Kuchinda to Bhubaneswar" : "Bhubaneswar to Kuchinda";

const scheduleSuccessText = (url: string, routeId: string, date: string, serverMessage: string) => {
  const direction = directionName(routeId);
  const removed = /Removed (\d+) old days/i.exec(serverMessage);
  const removedText = removed ? ` Removed ${removed[1]} old days.` : "";
  if (url === API_CONSTANTS.INTERNAL_RESCHEDULE_BUS) {
    return `Schedule rebuilt for ${direction} from ${date}.${removedText}`;
  }
  if (url === API_CONSTANTS.INTERNAL_DELETE_SCHEDULE) {
    const deleted = /Deleted (\d+)/i.exec(serverMessage);
    const count = deleted ? deleted[1] : "";
    return `Deleted ${count} schedule days for ${direction} from ${date}.`.replace("Deleted  ", "Deleted ");
  }
  return serverMessage;
};

const routeIdFor = (bus: string, code: string) => {
  const forward = FORWARD_CODES.has(code);
  if (bus === "1") {
    return forward ? "1" : "2";
  }
  return forward ? "3" : "4";
};

type BusRegistration = { id: number; busRegNum: string };

type DeskTicket = {
  seatNumber: string;
  passengerName: string;
  gender: string;
  mobileNumber: string;
  fromStop: string;
  toStop: string;
  bookedBy: string;
  bookedByName?: string;
  bookingStatus: string;
  bookingSource?: string;
  bookingRefId: string;
  price: number;
};

const ticketStatus = (status?: string) => {
  if (!status || status === "ONLINE" || status === "REDBUS" || status === "SMS") {
    return "Booked";
  }
  if (status === "INTERNAL_DESK") {
    return "Desk block";
  }
  return status;
};

const bookedThrough = (ticket: DeskTicket) => {
  const source = ticket.bookingSource || "Online";
  if (source === "Desk" && ticket.bookedByName) {
    return `Desk · ${ticket.bookedByName}`;
  }
  return source;
};

const prettyGender = (gender?: string) => {
  if (gender === "MALE") {
    return "Male";
  }
  if (gender === "FEMALE") {
    return "Female";
  }
  return gender || "";
};

const FALLBACK_BUSES: BusRegistration[] = [
  { id: 1, busRegNum: "OD-28A-6016" },
  { id: 2, busRegNum: "OD-15AB-0848" },
];

const ROUTE_SIDES = [
  { value: "1", busId: "1", side: "Bhubaneswar side" },
  { value: "2", busId: "1", side: "Kuchinda side" },
  { value: "3", busId: "2", side: "Bhubaneswar side" },
  { value: "4", busId: "2", side: "Kuchinda side" },
];

const errorText = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (typeof message === "string" && message.length > 0) {
      return message;
    }
    if (error.response?.status === 401 || error.response?.status === 403) {
      return typeof error.response?.data?.message === "string"
        ? error.response.data.message
        : "Sign in again. This desk session is not valid.";
    }
  }
  return fallback;
};

const InternalDesk: React.FC = () => {
  const [installReady, setInstallReady] = useState(canPromptInstall);
  const [installed, setInstalled] = useState(isStandalone);
  useEffect(() => subscribeInstall(() => {
    setInstallReady(canPromptInstall());
    setInstalled(isStandalone());
  }), []);
  const stored = readStoredSession();
  const [sessionToken, setSessionToken] = useState(stored.token);
  const [sessionUntil, setSessionUntil] = useState(stored.until);
  const [sessionMobile, setSessionMobile] = useState(stored.mobile);
  const [mobileNumberDraft, setMobileNumberDraft] = useState("");
  const [otp, setOtp] = useState("");
  const [otpDetails, setOtpDetails] = useState("");
  const [busy, setBusy] = useState(false);

  const [busId, setBusId] = useState("1");
  const [journeyDate, setJourneyDate] = useState("");
  const [fromTo, setFromTo] = useState("bk");
  const [mobileNumber, setMobileNumber] = useState("");
  const [locations, setLocations] = useState<IBusLocation[]>([]);
  const [buses, setBuses] = useState<BusRegistration[]>(FALLBACK_BUSES);
  const [layout, setLayout] = useState<{
    isAc?: boolean;
    seatLayout?: SEAT_LAYOUT;
    seatStyle?: SEAT_STYLE;
    seatingSeats?: SeatAvailability[];
    sleepingSeats?: SeatAvailability[];
    busRunning?: boolean;
    deskOpen?: boolean;
  } | null>(null);
  const [layoutMessage, setLayoutMessage] = useState("");
  const [layoutVersion, setLayoutVersion] = useState(0);
  const [selectedByType, setSelectedByType] = useState<{ seating: string[]; sleeping: string[] }>({
    seating: [],
    sleeping: [],
  });
  const [gendersBySeat, setGendersBySeat] = useState<Record<string, "m" | "f">>({});

  const [seatMode, setSeatMode] = useState<"book" | "block" | "unblock">("book");

  const [journeyRoute, setJourneyRoute] = useState("1");
  const [journeyOn, setJourneyOn] = useState("");
  const [dayStatus, setDayStatus] = useState<{
    scheduled: boolean;
    cancelled: boolean;
    onlineBookingStopped: boolean;
  } | null>(null);
  const [statusNote, setStatusNote] = useState("");
  const [statusVersion, setStatusVersion] = useState(0);

  const [scheduleRoute, setScheduleRoute] = useState("1");
  const [scheduleDate, setScheduleDate] = useState("");
  const [popup, setPopup] = useState("");
  const [tab, setTab] = useState<"block" | "tickets" | "journey" | "schedule" | "redbus">("block");
  const [redbusRoute, setRedbusRoute] = useState("1");
  const [redbusSeats, setRedbusSeats] = useState("");
  const [redbusByRoute, setRedbusByRoute] = useState<Record<string, string>>({});
  const [redbusEditing, setRedbusEditing] = useState(false);
  const [tickets, setTickets] = useState<DeskTicket[]>([]);
  const [ticketMessage, setTicketMessage] = useState("");

  const endSession = () => {
    localStorage.removeItem(SESSION_TOKEN);
    localStorage.removeItem(SESSION_UNTIL);
    localStorage.removeItem(SESSION_MOBILE);
    setSessionToken("");
    setSessionUntil(0);
    setSessionMobile("");
    setOtp("");
    setOtpDetails("");
  };

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(undefined, (error) => {
      const url = axios.isAxiosError(error) ? String(error.config?.url || "") : "";
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 401 &&
        url.includes("/internal/") &&
        !url.includes("verifyOtp")
      ) {
        endSession();
      }
      return Promise.reject(error);
    });
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const storeSession = (token: string, expiresAt: number, mobile: string) => {
    localStorage.setItem(SESSION_TOKEN, token);
    localStorage.setItem(SESSION_UNTIL, String(expiresAt));
    localStorage.setItem(SESSION_MOBILE, mobile);
    setSessionToken(token);
    setSessionUntil(expiresAt);
    setSessionMobile(mobile);
  };

  const requestOtp = async (event: React.FormEvent) => {
    event.preventDefault();
    const mobile = mobileNumberDraft.trim();
    if (!/^\d{10}$/.test(mobile)) {
      setPopup("Enter a 10 digit mobile number.");
      return;
    }
    setBusy(true);
    try {
      const response = await axios.post(API_CONSTANTS.INTERNAL_REQUEST_OTP, { mobileNumber: mobile });
      setOtpDetails(response.data?.details || "");
      setOtp("");
      setPopup("OTP sent.");
    } catch (error) {
      setOtpDetails("");
      setPopup(errorText(error, "OTP could not be sent."));
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (event: React.FormEvent) => {
    event.preventDefault();
    const mobile = mobileNumberDraft.trim();
    if (!otp.trim() || !otpDetails) {
      setPopup("Enter the OTP.");
      return;
    }
    setBusy(true);
    try {
      const response = await axios.post(API_CONSTANTS.INTERNAL_VERIFY_OTP, {
        mobileNumber: mobile,
        otp: otp.trim(),
        details: otpDetails,
      });
      storeSession(response.data.token, Number(response.data.expiresAt), mobile);
      setPopup("Signed in for 7 days.");
    } catch (error) {
      setPopup(errorText(error, "OTP is not valid."));
    } finally {
      setBusy(false);
    }
  };

  const postAction = async (url: string, body: object) => {
    const response = await axios.post(url, body, {
      headers: { "X-Desk-Session": sessionToken },
    });
    return response.data?.message || "Done";
  };

  const selectedSeats = useMemo(
    () => [...selectedByType.seating, ...selectedByType.sleeping],
    [selectedByType]
  );

  const rememberSeats = useCallback((kind: "seating" | "sleeping", seats: SeatAvailability[]) => {
    const numbers = seats
      .filter((seat) => seat.isSelected && seat.isAvailable)
      .map((seat) => String(seat.seatNumber));
    setSelectedByType((current) => {
      const existing = current[kind];
      if (existing.length === numbers.length && existing.every((seat, index) => seat === numbers[index])) {
        return current;
      }
      return { ...current, [kind]: numbers };
    });
  }, []);

  const ignoreSeats = useCallback(() => undefined, []);

  const onToggleSeats = useCallback(
    (seats: SeatAvailability[]) => rememberSeats("seating", seats),
    [rememberSeats]
  );
  const onToggleSleepers = useCallback(
    (seats: SeatAvailability[]) => rememberSeats("sleeping", seats),
    [rememberSeats]
  );

  const registrationFor = (id: string) =>
    buses.find((bus) => String(bus.id) === id)?.busRegNum || id;

  const canEditRedbus = sessionMobile === REDBUS_EDITOR_MOBILE;

  const routeOptions = ROUTE_SIDES.map((route) => ({
    value: route.value,
    label: `${registrationFor(route.busId)} · ${route.side}`,
  }));

  useEffect(() => {
    axios
      .get(API_CONSTANTS.BUS_LOCATIONS)
      .then((response) => setLocations(response.data))
      .catch(() => setPopup("Bus stops could not be loaded."));
    axios
      .get(API_CONSTANTS.BUS_REGISTRATIONS)
      .then((response) => {
        if (Array.isArray(response.data) && response.data.length > 0) {
          setBuses(response.data);
        }
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    setGendersBySeat((current) => {
      const next: Record<string, "m" | "f"> = {};
      selectedSeats.forEach((seat) => {
        next[seat] = current[seat] || "m";
      });
      const same =
        Object.keys(next).length === Object.keys(current).length &&
        selectedSeats.every((seat) => next[seat] === current[seat]);
      return same ? current : next;
    });
  }, [selectedSeats]);

  useEffect(() => {
    if (!journeyDate || !sessionToken || locations.length === 0) {
      setLayout(null);
      return;
    }
    const stops = FROM_TO_STOPS[fromTo];
    const fromStop = locations.find((stop) => stop.busStopName === stops.from);
    const toStop = locations.find((stop) => stop.busStopName === stops.to);
    if (!fromStop || !toStop) {
      setLayout(null);
      setPopup("Could not match boarding points for this route.");
      return;
    }
    let ignore = false;
    setLayoutMessage("Loading seat layout...");
    axios
      .post(
        API_CONSTANTS.INTERNAL_SEAT_LAYOUT,
        {
          busId,
          busRouteMappingId: routeIdFor(busId, fromTo),
          fromStop: String(fromStop.busStopId),
          toStop: String(toStop.busStopId),
          onDate: journeyDate,
        },
        { headers: { "X-Desk-Session": sessionToken } }
      )
      .then((response) => {
        if (ignore) {
          return;
        }
        setLayout(response.data);
        setSelectedByType({ seating: [], sleeping: [] });
        setGendersBySeat({});
        setLayoutMessage("");
      })
      .catch((error) => {
        if (ignore) {
          return;
        }
        setLayout(null);
        setLayoutMessage("");
        setPopup(errorText(error, "Seat layout could not be loaded."));
      });
    return () => {
      ignore = true;
    };
  }, [busId, fromTo, journeyDate, sessionToken, locations, layoutVersion]);

  useEffect(() => {
    if (tab !== "tickets" || !journeyDate || !sessionToken) {
      setTickets([]);
      setTicketMessage("");
      return;
    }
    let ignore = false;
    setTicketMessage("Loading booked tickets...");
    axios
      .post(
        API_CONSTANTS.INTERNAL_BOOKED_TICKETS,
        {
          busId: Number(busId),
          journeyDate,
          fromTo,
        },
        { headers: { "X-Desk-Session": sessionToken } }
      )
      .then((response) => {
        if (ignore) {
          return;
        }
        setTickets(Array.isArray(response.data) ? response.data : []);
        setTicketMessage("");
      })
      .catch((error) => {
        if (ignore) {
          return;
        }
        setTickets([]);
        setTicketMessage("");
        setPopup(errorText(error, "Booked tickets could not be loaded."));
      });
    return () => {
      ignore = true;
    };
  }, [tab, busId, fromTo, journeyDate, sessionToken, layoutVersion]);

  useEffect(() => {
    if (tab !== "journey" || !journeyOn || !sessionToken) {
      setDayStatus(null);
      setStatusNote("");
      return;
    }
    let ignore = false;
    setStatusNote("Checking this date...");
    axios
      .post(
        API_CONSTANTS.INTERNAL_DAY_STATUS,
        { busRouteMappingId: Number(journeyRoute), journeyDate: journeyOn },
        { headers: { "X-Desk-Session": sessionToken } }
      )
      .then((response) => {
        if (ignore) {
          return;
        }
        setDayStatus(response.data);
        setStatusNote("");
        if (!response.data?.scheduled) {
          setPopup("This bus is not scheduled on that date.");
        }
      })
      .catch((error) => {
        if (ignore) {
          return;
        }
        setDayStatus(null);
        setStatusNote("");
        setPopup(errorText(error, "Could not load this date."));
      });
    return () => {
      ignore = true;
    };
  }, [tab, journeyOn, journeyRoute, sessionToken, statusVersion]);

  useEffect(() => {
    if (tab === "redbus" && !canEditRedbus) {
      setTab("block");
    }
  }, [tab, canEditRedbus]);

  useEffect(() => {
    if (tab !== "redbus" || !canEditRedbus || !sessionToken) {
      return;
    }
    let ignore = false;
    axios
      .post(API_CONSTANTS.INTERNAL_REDBUS_SEATS, {}, { headers: { "X-Desk-Session": sessionToken } })
      .then((response) => {
        if (ignore) {
          return;
        }
        const next: Record<string, string> = {};
        if (Array.isArray(response.data)) {
          response.data.forEach((row: { busRouteMappingId: number; seatNumbers?: string }) => {
            next[String(row.busRouteMappingId)] = row.seatNumbers || "";
          });
        }
        setRedbusByRoute(next);
        setRedbusSeats(next[redbusRoute] || "");
      })
      .catch((error) => {
        if (!ignore) {
          setPopup(errorText(error, "Redbus seats could not be loaded."));
        }
      });
    return () => {
      ignore = true;
    };
  }, [tab, canEditRedbus, sessionToken, redbusRoute]);

  const busNotRunning = layout?.busRunning === false;
  const deskClosed = layout?.deskOpen === false;

  const seatLayout = useMemo(() => {
    if (!layout) {
      return null;
    }
    const prepare = (seat: SeatAvailability): SeatAvailability => {
      if (seatMode === "unblock") {
        return { ...seat, isAvailable: Boolean(seat.deskBlocked) };
      }
      return { ...seat, isAvailable: Boolean(seat.isAvailable) && !seat.isBooked };
    };
    return {
      ...layout,
      seatingSeats: (layout.seatingSeats || []).map(prepare),
      sleepingSeats: (layout.sleepingSeats || []).map(prepare),
    };
  }, [layout, seatMode]);

  const saveRedbus = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const message = await postAction(API_CONSTANTS.INTERNAL_SAVE_REDBUS_SEATS, {
        busRouteMappingId: Number(redbusRoute),
        seatNumbers: redbusSeats,
      });
      setRedbusByRoute((current) => ({ ...current, [redbusRoute]: redbusSeats }));
      setRedbusEditing(false);
      setPopup(message);
    } catch (error) {
      setPopup(errorText(error, "Redbus seats could not be saved."));
    } finally {
      setBusy(false);
    }
  };

  const saveSeats = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!journeyDate) {
      setPopup("Choose the journey date.");
      return;
    }
    if (selectedSeats.length === 0) {
      setPopup("Select at least one seat.");
      return;
    }
    if (!mobileNumber.trim()) {
      setPopup("Enter the passenger mobile.");
      return;
    }
    if (!/^\d{10}$/.test(mobileNumber.trim())) {
      setPopup("Passenger mobile must be 10 digits.");
      return;
    }
    const isBook = seatMode === "book";
    setBusy(true);
    try {
      const message = await postAction(
        isBook ? API_CONSTANTS.INTERNAL_BOOK_SEATS : API_CONSTANTS.INTERNAL_BLOCK_SEATS,
        {
          busId: Number(busId),
          journeyDate,
          seats: selectedSeats.join(","),
          fromTo,
          mobileNumber: mobileNumber.trim(),
          genders: selectedSeats.map((seat) => gendersBySeat[seat] || "m").join(","),
        }
      );
      setPopup(message);
      setLayoutVersion((version) => version + 1);
    } catch (error) {
      setPopup(errorText(error, isBook ? "Seats could not be booked." : "Seats could not be blocked."));
    } finally {
      setBusy(false);
    }
  };

  const unblockSeats = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!journeyDate || selectedSeats.length === 0) {
      setPopup("Choose a date and select the booked seats to release.");
      return;
    }
    setBusy(true);
    try {
      const message = await postAction(API_CONSTANTS.INTERNAL_UNBLOCK_SEATS, {
        busId: Number(busId),
        journeyDate,
        seats: selectedSeats.join(","),
        fromTo,
      });
      setPopup(message);
      setLayoutVersion((version) => version + 1);
    } catch (error) {
      setPopup(errorText(error, "Seats could not be released."));
    } finally {
      setBusy(false);
    }
  };

  const runJourneyAction = async (url: string) => {
    if (!sessionToken) {
      setPopup("Sign in with OTP before using the desk.");
      return;
    }
    if (!journeyOn) {
      setPopup("Choose the journey date.");
      return;
    }
    setBusy(true);
    try {
      const message = await postAction(url, {
        busRouteMappingId: Number(journeyRoute),
        journeyDate: journeyOn,
      });
      setPopup(journeyResultText(url, message));
      setStatusVersion((version) => version + 1);
    } catch (error) {
      setPopup(errorText(error, "This action could not be completed."));
    } finally {
      setBusy(false);
    }
  };

  const journeyResultText = (url: string, serverMessage: string) => {
    const route = ROUTE_SIDES.find((item) => item.value === journeyRoute);
    const bus = registrationFor(route?.busId || "");
    const side = route?.side || "selected side";
    const date = new Date(`${journeyOn}T00:00:00`).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    const trip = `${bus} from the ${side} on ${date}`;
    const already = /already/i.test(serverMessage);
    if (url === API_CONSTANTS.INTERNAL_CANCEL_BUS) {
      return already ? `${trip} is already cancelled.` : `${trip} is cancelled.`;
    }
    if (url === API_CONSTANTS.INTERNAL_CONTINUE_BUS) {
      return already ? `${trip} is already running.` : `${trip} is continued.`;
    }
    if (url === API_CONSTANTS.INTERNAL_STOP_ONLINE) {
      return already ? `Online booking for ${trip} is already stopped.` : `Online booking for ${trip} is stopped.`;
    }
    return already ? `Online booking for ${trip} is already open.` : `Online booking for ${trip} is started.`;
  };

  const runScheduleAction = async (
    event: { preventDefault: () => void },
    url: string,
    routeId: string,
    date: string
  ) => {
    event.preventDefault();
    if (!sessionToken) {
      setPopup("Sign in with OTP before using the desk.");
      return;
    }
    if (!date) {
      setPopup("Choose the journey date.");
      return;
    }
    setBusy(true);
    try {
      const message = await postAction(url, {
        busRouteMappingId: Number(routeId),
        journeyDate: date,
      });
      setPopup(scheduleSuccessText(url, routeId, date, message));
    } catch (error) {
      setPopup(errorText(error, "This action could not be completed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`container pageContainer ${styles.desk}`}>
      <div className={styles.titleRow}>
        <h1>Internal desk</h1>
        {!installed && installReady && (
          <button type="button" className={styles.secondary} onClick={() => { void promptInstall(); }}>
            Install app
          </button>
        )}
      </div>
      {!installed && !installReady && (
        <p className={styles.installNote}>
          {isIosBrowser()
            ? "To install, tap Share, then Add to Home Screen."
            : "To install, use the install icon in the address bar, or the browser menu, then Install app."}
        </p>
      )}
      <p className={styles.lead}>
        Book or release seats, review booked tickets, cancel a bus and continue it later, or stop and start online booking. This page is not linked from the public site.
      </p>

      {!sessionToken && (
      <form className={styles.keyCard} onSubmit={otpDetails ? verifyOtp : requestOtp}>
        <label htmlFor="deskMobile">Mobile number</label>
        <input
          id="deskMobile"
          inputMode="numeric"
          autoComplete="tel"
          value={mobileNumberDraft}
          onChange={(event) => {
            setMobileNumberDraft(event.target.value);
            setOtpDetails("");
            setOtp("");
          }}
          placeholder="10 digit mobile"
        />
        {otpDetails && (
          <>
            <label htmlFor="deskOtp">OTP</label>
            <input
              id="deskOtp"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={otp}
              onChange={(event) => setOtp(event.target.value)}
              placeholder="OTP"
            />
          </>
        )}
        <p className={styles.hint}>
          {otpDetails ? "Enter the OTP sent to this mobile." : "Only an allowed mobile number can open this desk."}
        </p>
        <div className={styles.actions}>
          <button type="submit" disabled={busy}>
            {otpDetails ? "Verify OTP" : "Get OTP"}
          </button>
        </div>
      </form>
      )}

      {sessionToken && (
      <div className={styles.keyCard}>
        <p className={styles.hint}>
          Signed in as {sessionMobile} until {new Date(sessionUntil).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}.
        </p>
        <div className={styles.actions}>
          <button type="button" className={styles.secondary} onClick={endSession}>Sign out</button>
        </div>
      </div>
      )}

      {sessionToken && (
      <>
      <div className={styles.tabs} role="tablist">
        <button type="button" role="tab" aria-selected={tab === "block"} className={tab === "block" ? styles.tabActive : styles.tab} onClick={() => setTab("block")}>
          Seats
        </button>
        <button type="button" role="tab" aria-selected={tab === "tickets"} className={tab === "tickets" ? styles.tabActive : styles.tab} onClick={() => setTab("tickets")}>
          Booked tickets
        </button>
        <button type="button" role="tab" aria-selected={tab === "journey"} className={tab === "journey" ? styles.tabActive : styles.tab} onClick={() => setTab("journey")}>
          Action on Journey
        </button>
        <button type="button" role="tab" aria-selected={tab === "schedule"} className={tab === "schedule" ? styles.tabActive : styles.tab} onClick={() => setTab("schedule")}>
          Schedule
        </button>
        {canEditRedbus && (
          <button type="button" role="tab" aria-selected={tab === "redbus"} className={tab === "redbus" ? styles.tabActive : styles.tab} onClick={() => setTab("redbus")}>
            Redbus seats
          </button>
        )}
      </div>

      {tab === "block" && (
      <form className={styles.panel} onSubmit={seatMode === "unblock" ? unblockSeats : saveSeats}>
        <div className={styles.modeSwitch} role="group" aria-label="Seat action">
          <button
            type="button"
            className={seatMode === "book" ? styles.modeActive : styles.mode}
            onClick={() => {
              setSeatMode("book");
              setSelectedByType({ seating: [], sleeping: [] });
            }}
          >
            Book seats
          </button>
          <button
            type="button"
            className={seatMode === "unblock" ? styles.modeActive : styles.mode}
            onClick={() => {
              setSeatMode("unblock");
              setSelectedByType({ seating: [], sleeping: [] });
            }}
          >
            Release seats
          </button>
        </div>
        <p className={styles.hint}>
          {seatMode === "book"
            ? "Pick open seats. Each selected seat starts as male. This confirms the seats immediately and sends the booking SMS to the passenger mobile. Redbus seats stay closed until the journey date."
            : seatMode === "block"
              ? "Pick open seats. Each selected seat starts as male. This confirms the seats immediately and does not send an SMS."
              : "Pick seats booked from this desk. Online and SMS bookings stay booked. This sends a cancellation SMS to the passenger mobile."}
        </p>
        <div className={styles.grid}>
          <div>
            <label htmlFor="busId">Bus</label>
            <select id="busId" value={busId} onChange={(event) => setBusId(event.target.value)}>
              {buses.map((bus) => (
                <option key={bus.id} value={bus.id}>
                  {bus.busRegNum}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="blockDate">Journey date</label>
            <input
              id="blockDate"
              type="date"
              value={journeyDate}
              onChange={(event) => setJourneyDate(event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="fromTo">From and to</label>
            <select id="fromTo" value={fromTo} onChange={(event) => setFromTo(event.target.value)}>
              {FROM_TO_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {seatMode !== "unblock" && (
            <div>
              <label htmlFor="mobileNumber">Passenger mobile</label>
              <input
                id="mobileNumber"
                inputMode="numeric"
                value={mobileNumber}
                onChange={(event) => setMobileNumber(event.target.value)}
                placeholder="10 digit mobile"
              />
            </div>
          )}
        </div>
        <div className={seatMode === "unblock" && !deskClosed ? `${styles.layoutWrap} ${styles.coach} ${styles.releaseSeats}` : `${styles.layoutWrap} ${styles.coach}${deskClosed ? ` ${styles.viewOnly}` : ""}`}>
          {!sessionToken && <p className={styles.hint}>Sign in, then choose a date to load the seat layout.</p>}
          {sessionToken && !journeyDate && <p className={styles.hint}>Choose a journey date to load the seat layout.</p>}
          {layoutMessage && <p className={styles.hint}>{layoutMessage}</p>}
          {busNotRunning && (
            <p className={styles.notRunning}>This bus is not running on this date for this route.</p>
          )}
          {deskClosed && !busNotRunning && (
            <p className={styles.notRunning}>This journey has ended. Seats are read only.</p>
          )}
          {seatLayout && !busNotRunning && (
            <>
              <div className={styles.legend}>
                <span>Open seats can be booked.</span>
                <span>Only seats booked from this desk can be released.</span>
              </div>
              <div className={styles.frontTag}>
                <span className={styles.wheel} aria-hidden="true" />
                Front of the bus
              </div>
              <BusLayout
                key={`${busId}-${fromTo}-${journeyDate}-${layoutVersion}-${seatMode}`}
                isAc={Boolean(seatLayout.isAc)}
                seatLayout={seatLayout.seatLayout || SEAT_LAYOUT.SINGLE_LAYER_SEATING_2X2}
                seatStyle={seatLayout.seatStyle || SEAT_STYLE.SEATING}
                seatingSeats={seatLayout.seatingSeats || []}
                sleepingSeats={seatLayout.sleepingSeats || []}
                isSingleFirstSeats={false}
                onToggleSeats={onToggleSeats}
                onToggleSleepers={onToggleSleepers}
              />
            </>
          )}
          {seatMode !== "unblock" && selectedSeats.length > 0 && (
            <div className={styles.genderList}>
              {selectedSeats.map((seat) => (
                <label key={seat} className={styles.genderItem}>
                  Seat {seat}
                  <select
                    aria-label={`Gender for seat ${seat}`}
                    value={gendersBySeat[seat] || "m"}
                    onChange={(event) =>
                      setGendersBySeat((current) => ({
                        ...current,
                        [seat]: event.target.value === "f" ? "f" : "m",
                      }))
                    }
                  >
                    <option value="m">M</option>
                    <option value="f">F</option>
                  </select>
                </label>
              ))}
            </div>
          )}
        </div>
        <div className={styles.actions}>
          <button type="submit" className={seatMode === "unblock" ? styles.secondary : undefined} disabled={busy || !sessionToken || busNotRunning || deskClosed}>
            {busy && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}
            {seatMode === "book" ? "Book seats" : seatMode === "block" ? "Block seats" : "Release seats"}
          </button>
          {selectedSeats.length > 0 && (
            <span className={styles.hint}>
              {selectedSeats.length} seat{selectedSeats.length === 1 ? "" : "s"} selected
            </span>
          )}
        </div>
      </form>
      )}

      {tab === "tickets" && (
      <div className={styles.panel}>
        <p className={styles.hint}>
          Booked seats for this bus side on the chosen date, including other boarding points on the same coach.
        </p>
        <div className={styles.grid}>
          <div>
            <label htmlFor="ticketBus">Bus</label>
            <select id="ticketBus" value={busId} onChange={(event) => setBusId(event.target.value)}>
              {buses.map((bus) => (
                <option key={bus.id} value={bus.id}>
                  {bus.busRegNum}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="ticketDate">Journey date</label>
            <input
              id="ticketDate"
              type="date"
              value={journeyDate}
              onChange={(event) => setJourneyDate(event.target.value)}
            />
          </div>
          <div className={styles.full}>
            <label htmlFor="ticketFromTo">From and to</label>
            <select id="ticketFromTo" value={fromTo} onChange={(event) => setFromTo(event.target.value)}>
              {FROM_TO_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {!sessionToken && <p className={styles.hint}>Sign in, then choose a date.</p>}
        {sessionToken && !journeyDate && <p className={styles.hint}>Choose a journey date to load the seats and tickets.</p>}
        {(layoutMessage || ticketMessage) && <p className={styles.hint}>{ticketMessage || layoutMessage}</p>}
        {journeyDate && busNotRunning && !layoutMessage && (
          <p className={styles.notRunning}>Bus not running on this date</p>
        )}
        {journeyDate && !busNotRunning && (
        <div className={styles.ticketBoard}>
          <div className={`${styles.layoutWrap} ${styles.coach} ${styles.viewOnly}`}>
            {layout && (
              <>
              <div className={styles.frontTag}>
                <span className={styles.wheel} aria-hidden="true" />
                Front of the bus
              </div>
              <BusLayout
                key={`tickets-${busId}-${fromTo}-${journeyDate}-${layoutVersion}`}
                isAc={Boolean(layout.isAc)}
                seatLayout={layout.seatLayout || SEAT_LAYOUT.SINGLE_LAYER_SEATING_2X2}
                seatStyle={layout.seatStyle || SEAT_STYLE.SEATING}
                seatingSeats={layout.seatingSeats || []}
                sleepingSeats={layout.sleepingSeats || []}
                isSingleFirstSeats={false}
                onToggleSeats={ignoreSeats}
                onToggleSleepers={ignoreSeats}
              />
              </>
            )}
          </div>
          <div className={styles.ticketTableWrap}>
            <table className={styles.ticketTable}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Seat</th>
                  <th>Passenger</th>
                  <th>Mobile</th>
                  <th>Booked through</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {tickets.length === 0 && !ticketMessage && (
                  <tr>
                    <td colSpan={8}>No booked tickets for this date.</td>
                  </tr>
                )}
                {tickets.map((ticket, index) => (
                  <tr key={`${ticket.bookingRefId}-${ticket.seatNumber}`}>
                    <td>#{index + 1}</td>
                    <td>{ticket.seatNumber}</td>
                    <td>
                      {ticket.passengerName || "—"}
                      {prettyGender(ticket.gender) ? ` · ${prettyGender(ticket.gender)}` : ""}
                    </td>
                    <td>{ticket.mobileNumber || "—"}</td>
                    <td>{bookedThrough(ticket)}</td>
                    <td>{ticket.fromStop || "—"}</td>
                    <td>{ticket.toStop || "—"}</td>
                    <td>{ticketStatus(ticket.bookingStatus)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </div>
      )}

      {tab === "journey" && (
      <div className={styles.panel}>
        <p className={styles.hint}>
          Cancel hides the bus and refuses online booking. Continue puts that same date back. Stop and start only change whether passengers can book online. Tickets already booked stay as they are.
        </p>
        <div className={styles.grid}>
          <div>
            <label htmlFor="journeyRoute">Bus and direction</label>
            <select id="journeyRoute" value={journeyRoute} onChange={(event) => setJourneyRoute(event.target.value)}>
              {routeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="journeyOn">Journey date</label>
            <input
              id="journeyOn"
              type="date"
              value={journeyOn}
              onChange={(event) => setJourneyOn(event.target.value)}
            />
          </div>
        </div>
        {statusNote && <p className={styles.hint}>{statusNote}</p>}
        <div className={styles.statusList}>
          <div className={styles.statusRow}>
            <div>
              <span className={dayStatus?.cancelled ? styles.badgeWarn : styles.badgeOk}>
                {!dayStatus?.scheduled ? "Trip" : dayStatus.cancelled ? "Cancelled" : "Running"}
              </span>
              <p className={styles.hint}>
                Cancel removes this bus from search for the selected date. Continue puts that same date back.
              </p>
            </div>
            <div className={styles.actions}>
              <button type="button" className={styles.danger} disabled={busy} onClick={() => runJourneyAction(API_CONSTANTS.INTERNAL_CANCEL_BUS)}>
                Cancel bus
              </button>
              <button type="button" disabled={busy} onClick={() => runJourneyAction(API_CONSTANTS.INTERNAL_CONTINUE_BUS)}>
                Continue bus
              </button>
            </div>
          </div>
          <div className={styles.statusRow}>
            <div>
              <span className={dayStatus?.cancelled || dayStatus?.onlineBookingStopped ? styles.badgeWarn : styles.badgeOk}>
                {!dayStatus?.scheduled ? "Online booking" : dayStatus.cancelled ? "Online booking closed" : dayStatus.onlineBookingStopped ? "Online booking stopped" : "Online booking open"}
              </span>
              <p className={styles.hint}>
                Stop and start only change whether passengers can book this date online. Tickets already booked stay as they are.
              </p>
            </div>
            <div className={styles.actions}>
              <button type="button" className={styles.secondary} disabled={busy} onClick={() => runJourneyAction(API_CONSTANTS.INTERNAL_STOP_ONLINE)}>
                Stop online booking
              </button>
              <button type="button" disabled={busy} onClick={() => runJourneyAction(API_CONSTANTS.INTERNAL_START_ONLINE)}>
                Start online booking
              </button>
            </div>
          </div>
        </div>
      </div>
      )}

      {tab === "schedule" && (
      <form
        className={styles.panel}
        onSubmit={(event) => event.preventDefault()}
      >
        <p className={styles.hint}>
          Delete removes this bus from the selected date onward. Reschedule does that and then creates a new schedule from the same date. Booked dates block both actions.
        </p>
        <div className={styles.grid}>
          <div>
            <label htmlFor="scheduleRoute">Bus and direction</label>
            <select id="scheduleRoute" value={scheduleRoute} onChange={(event) => setScheduleRoute(event.target.value)}>
              {routeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="scheduleDate">From date</label>
            <input
              id="scheduleDate"
              type="date"
              value={scheduleDate}
              onChange={(event) => setScheduleDate(event.target.value)}
            />
          </div>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            disabled={busy || !sessionToken}
            onClick={(event) =>
              runScheduleAction(event, API_CONSTANTS.INTERNAL_DELETE_SCHEDULE, scheduleRoute, scheduleDate)
            }
          >
            Delete schedule
          </button>
          <button
            type="button"
            className={styles.secondary}
            disabled={busy || !sessionToken}
            onClick={(event) =>
              runScheduleAction(event, API_CONSTANTS.INTERNAL_RESCHEDULE_BUS, scheduleRoute, scheduleDate)
            }
          >
            Reschedule
          </button>
        </div>
      </form>
      )}

      {tab === "redbus" && canEditRedbus && (
      <form className={styles.panel} onSubmit={saveRedbus}>
        <p className={styles.hint}>
          Seats listed here stay with Redbus and cannot be booked on QuickBusRide.
        </p>
        <div className={styles.grid}>
          <div>
            <label htmlFor="redbusRoute">Bus and direction</label>
            <select
              id="redbusRoute"
              value={redbusRoute}
              onChange={(event) => {
                setRedbusEditing(false);
                setRedbusRoute(event.target.value);
              }}
            >
              {routeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.full}>
            <label htmlFor="redbusSeats">Redbus seat numbers</label>
            {redbusEditing ? (
              <textarea
                id="redbusSeats"
                className={styles.seatBox}
                value={redbusSeats}
                onChange={(event) => setRedbusSeats(event.target.value)}
                placeholder="3,4,S2"
              />
            ) : (
              <p id="redbusSeats" className={styles.seatReadout}>
                {redbusSeats.trim() ? redbusSeats : "None. Every seat on this route can be booked."}
              </p>
            )}
          </div>
        </div>
        <div className={styles.actions}>
          {redbusEditing ? (
            <>
              <button type="submit" disabled={busy || !sessionToken}>
                Save Redbus seats
              </button>
              <button
                type="button"
                className={styles.secondary}
                disabled={busy}
                onClick={() => {
                  setRedbusSeats(redbusByRoute[redbusRoute] || "");
                  setRedbusEditing(false);
                }}
              >
                Cancel
              </button>
            </>
          ) : (
            <button type="button" disabled={!sessionToken} onClick={() => setRedbusEditing(true)}>
              Edit seats
            </button>
          )}
        </div>
      </form>
      )}
      </>
      )}

      {popup && (
        <div className={styles.popupBackdrop} role="presentation" onClick={() => setPopup("")}>
          <div
            className={styles.popup}
            role="dialog"
            aria-modal="true"
            aria-labelledby="journeyPopup"
            onClick={(event) => event.stopPropagation()}
          >
            <p id="journeyPopup">{popup}</p>
            <button type="button" onClick={() => setPopup("")}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InternalDesk;
